import CircularButton from "@/components/CircularButton";
import { FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import Link from "@/components/wrappers/Link";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { borderRadius } from "@/utils/borderRadius";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { AI_TOOL } from "@knowt/syncing/constants/ai-tools";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { updateUserInfo } from "@knowt/syncing/hooks/user/utils";
import { createQueryStrings } from "@knowt/syncing/utils/genericUtils";
import Box from "@mui/material/Box";

import { StarIcon } from "lucide-react";
import Image from "next/image";
import toast from "react-hot-toast";

const TeacherAIToolCard = ({ tool, folderId, classId }: { tool: AI_TOOL; folderId?: string; classId?: string }) => {
    const { checkAuth } = useCheckAuth();
    const { user } = useCurrentUser();

    const favoriteTools = user?.favTools ?? [];
    const isFavorite = !!favoriteTools && favoriteTools.includes(tool.tool);

    const updateUserFavorites = async (favTools: string[]) => {
        try {
            await updateUserInfo(
                {
                    favTools: favTools.length ? favTools : null,
                },
                true
            );
        } catch {
            toast.error("Something went wrong, please try again later.");
        }
    };

    const handleStarTool = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const isAuthenticated = await checkAuth({
            event: AuthEvent.FAVORITE_AI_TOOL,
        });

        if (!isAuthenticated) return;

        const updatedFavTools = isFavorite
            ? favoriteTools.filter(toolKey => toolKey !== tool.tool)
            : [...(favoriteTools ?? []), tool.tool];

        toast.success(isFavorite ? "Removed from favorites" : "Added to favorites");

        updateUserFavorites(updatedFavTools);
    };

    return (
        <Link
            id={tool.tool}
            href={tool.href + createQueryStrings({ folderId, classId })}
            key={tool.title}
            style={{ textDecoration: "none" }}>
            <Box
                component={"div"}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    color: themeColors.pureBlack,
                    backgroundColor: themeColors.neutralWhite,
                    borderRadius: borderRadius.card,
                    padding: "2rem",
                    height: "100%",
                    cursor: "pointer",
                    "&:hover": {
                        outline: `1px solid ${themeColors.neutralBlack}`,
                    },
                }}>
                <FlexRowAlignCenter
                    style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: spacing.XS_2 }}>
                    <FlexColumnAlignJustifyCenter
                        style={{
                            backgroundColor: tool.color,
                            borderRadius: "0.8rem",
                            height: "3.5rem",
                            width: "3.5rem",
                        }}>
                        <Image src={`/images/aiTools/${tool.tool}.svg`} alt={tool.title} width={20} height={20} />
                    </FlexColumnAlignJustifyCenter>

                    <CircularButton
                        data-prevent-progress="true"
                        component={"div"}
                        onClick={handleStarTool}
                        style={{
                            backgroundColor: themeColors.background,
                            width: "3.1rem",
                            height: "3.1rem",
                        }}>
                        <StarIcon
                            size={iconSizes.SM_S}
                            fill={isFavorite ? themeColors.primary3 : themeColors.neutralWhite}
                            color={isFavorite ? themeColors.primary3 : themeColors.neutralBlack}
                        />
                    </CircularButton>
                </FlexRowAlignCenter>
                <span className="secondaryTextBold1" style={{ marginBottom: spacing.XS, width: "100%" }}>
                    {tool.title}
                </span>
                <span
                    className="secondaryText1"
                    style={{
                        color: themeColors.neutralBlack,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        whiteSpace: "normal",
                    }}>
                    {tool.description}
                </span>
            </Box>
        </Link>
    );
};

export default TeacherAIToolCard;
